import Titulo from '../../components/tipografia/Titulo';
import styles from './page.module.css';
import LayoutUsuario from '../../components/navegacao/LayoutUsuario';
import Descricao from '../../components/tipografia/Descricao';
import HBox from '../../components/layout/HBox';
import Botao from '../../components/Botao';
import VBox from '../../components/layout/VBox';
import CampoTexto from '../../components/Formulario/CampoTexto';
import Icon from '@mdi/react';
import { mdiMagnify, mdiPlus, mdiPen, mdiTrashCan } from '@mdi/js';
import { useMutation, useQuery } from 'react-query';
import axios from 'axios';
import React from 'react';
import { useDebounce } from '../chat/useDebounce';
import Skeleton from '../../components/layout/Skeleton';
import { toast } from 'react-toastify';
import Modal from '../../components/layout/Modal';
import AlertBox from '../../components/layout/AlertBox';
import DataTable, { DataTableHeader } from '../../components/layout/DataTable';
import { FormCentroCusto } from './FormCentroCusto';

const CentroCustoPage = () => {
  const [search, setSearch] = React.useState<string>('');
  const [selected, setSelected] = React.useState<any>(null);
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  const [showDeleteModal, setShowDeleteModal] = React.useState<number | null>(null);
  const [showFormModal, setShowFormModal] = React.useState<boolean>(false);
  const debouncedSearch = useDebounce(search, 500);

  const query = useQuery(
    ['centro_custo:search', debouncedSearch],
    async () => {
      const res = await axios.get('/api/centro_custo', {
        params: { txt: debouncedSearch || undefined, t: 50 },
      });
      return res.data;
    },
  );

  const changeMutation = useMutation(
    async (form: any) => {
      setFieldErrors({});
      if (form.id) {
        return await axios.put(`/api/centro_custo/${form.id}`, form);
      }
      return await axios.post('/api/centro_custo', form);
    },
    {
      onSuccess: () => {
        toast('Centro de custo salvo com sucesso', {
          type: 'success', position: 'top-center', autoClose: 5000,
        });
        query.refetch();
        setShowFormModal(false);
        setSelected(null);
      },
      onError: (error: any) => {
        setFieldErrors(error.response?.data?.fieldErrors || {});
        toast(error.response?.data?.msg ?? 'Erro ao salvar centro de custo', {
          type: 'error', position: 'top-center', autoClose: 5000,
        });
      },
    },
  );

  const deleteMutation = useMutation(
    async (id: number) => await axios.delete(`/api/centro_custo/${id}`),
    {
      onSuccess: () => {
        toast('Centro de custo excluído com sucesso', {
          type: 'success', position: 'top-center', autoClose: 5000,
        });
        query.refetch();
        setShowDeleteModal(null);
      },
      onError: (error: any) => {
        toast(error.response?.data?.msg || 'Erro ao excluir centro de custo', {
          type: 'error', position: 'top-center', autoClose: 5000,
        });
      },
    },
  );

  const handleOpenForm = (form: any = null) => {
    setSelected(form);
    setShowFormModal(true);
  };

  const handleCloseForm = () => {
    setSelected(null);
    setShowFormModal(false);
  };

  const handleFormConfirm = () => {
    if (selected) {
      changeMutation.mutate(selected);
    }
  };

  const renderContent = () => {
    if (query.isLoading) {
      return (
        <VBox gap="8px">
          <Skeleton format="box" />
          <Skeleton format="box" />
          <Skeleton format="box" />
        </VBox>
      );
    }

    if (query.error) {
      return (
        <AlertBox variant="danger">
          <p>Erro ao carregar centros de custo: {(query.error as any)?.message || 'Erro desconhecido'}</p>
        </AlertBox>
      );
    }

    const headers: DataTableHeader[] = [
      { label: 'Nome', width: '80%', column: 'nome' },
      {
        label: 'Ações',
        width: '120px',
        stickRight: true,
        alignRight: true,
        render: (item: any) => (
          <HBox gap="8px" style={{ justifyContent: 'flex-end' }}>
            <Botao variant="none-primary" onClick={() => handleOpenForm(item)} title="Editar" icon={mdiPen} />
            <Botao variant="none-danger" onClick={() => setShowDeleteModal(item.id)} title="Apagar" icon={mdiTrashCan} />
          </HBox>
        ),
      },
    ];

    return <DataTable cabecalhos={headers} linhas={query.data.result} />;
  };

  return (
    <LayoutUsuario>
      <div className={styles.container}>
        <VBox gap="24px">
          <HBox>
            <div className={styles.headerTitulo}>
              <Titulo>Centro de Custos</Titulo>
              <Descricao>
                Agrupe lançamentos por área ou projeto do seu negócio.
              </Descricao>
            </div>
            <div className={styles.headerAcoes}>
              <Botao
                variant="primary"
                onClick={() => handleOpenForm({ nome: '' })}
                title="Novo centro de custo"
                icon={mdiPlus}
              >
                Novo centro de custo
              </Botao>
            </div>
          </HBox>

          <CampoTexto
            placeholder="Buscar centro de custo..."
            value={search}
            onChange={setSearch}
            prepend={<Icon path={mdiMagnify} size={1} color="var(--tc-color-gray-500)" />}
          />

          <div>{renderContent()}</div>
        </VBox>
      </div>

      <Modal
        instanceId="centro-custo-delete-modal"
        show={!!showDeleteModal}
        onClose={() => setShowDeleteModal(null)}
        size="sm"
        title="Confirmar exclusão"
        cancelProps={{ label: 'Cancelar' }}
        confirmProps={{ label: 'Confirmar', variant: 'danger', disabled: deleteMutation.isLoading }}
        onConfirm={() => showDeleteModal && deleteMutation.mutate(showDeleteModal)}
        onCancel={() => setShowDeleteModal(null)}
      >
        <p>Tem certeza que deseja excluir este centro de custo?</p>
        <p>Esta ação não pode ser desfeita.</p>
      </Modal>

      <Modal
        instanceId="centro-custo-form-modal"
        show={showFormModal}
        onClose={handleCloseForm}
        onCancel={handleCloseForm}
        onConfirm={handleFormConfirm}
        title={selected?.id > 0 ? 'Editar centro de custo' : 'Novo centro de custo'}
        confirmProps={{ label: 'Salvar', variant: 'primary', disabled: changeMutation.isLoading }}
        cancelProps={{ label: 'Cancelar' }}
      >
        <FormCentroCusto
          value={selected}
          onChange={setSelected}
          fieldErrors={fieldErrors}
        />
      </Modal>
    </LayoutUsuario>
  );
};

export default CentroCustoPage;
